import React from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';

import LayoutEmpty from '../components/layouts/LayoutEmpty';
import LayoutMain from '../components/layouts/LayoutMain';
import LayoutAdmin from '../components/layouts/LayoutAdmin';
import ErrorPage from '../pages/404';
import Cards from '../pages/cards';
import Customers from '../pages/customers';
import Dashboard from '../pages/dashboard';
import Orders from '../pages/orders';
import DestPoint from '../pages/destPoint';
import DestPointDetails from '../pages/destPointDetails';
import Drivers from '../pages/drivers';
import GoogleCallback from '../pages/googleCallback';
import Login from '../pages/login';
import MicrosoftCallback from '../pages/microsoftCallback';
import ResetPassword from '../pages/reset-password';
import Products from '../pages/products';
import ProductsGroups from '../pages/productsGroups';
import Results from '../pages/results';
import Spy from '../pages/spy';
import Vehicles from '../pages/vehicles';
import AdminDashboard from '../pages/admin/adminDashboard';
import AdminHistory from '../pages/admin/adminHistory';
import AdminLicenses from '../pages/admin/adminLicenses';
import AdminUsers from '../pages/admin/adminUsers';
import AdminAccessGroups from '../pages/admin/adminAccessGroups';
import AdminAudit from '../pages/admin/adminAudit';

type RouterType = {
 path: string;
 element: React.ReactNode;
 layout: React.ElementType;
 children?: RouterType[];
 requiresAuth?: boolean;
};

export const router = [
 {
  path: '/',
  element: <Results />,
  layout: LayoutMain,
  requiresAuth: true,
 },
 {
  path: '/login',
  element: <Login />,
  layout: LayoutEmpty,
  requiresAuth: false,
 },
 {
  path: '/reset-password',
  element: <ResetPassword />,
  layout: LayoutEmpty,
  requiresAuth: false,
 },
 {
  path: '/microsoft-callback',
  element: <MicrosoftCallback />,
  layout: LayoutEmpty,
  requiresAuth: false,
 },
 {
  path: '/google-callback',
  element: <GoogleCallback />,
  layout: LayoutEmpty,
  requiresAuth: false,
 },
 {
  path: '/results',
  layout: LayoutMain,
  element: <Results />,
 },
 {
  path: '/customers',
  layout: LayoutMain,
  element: <Customers />,
 },
 {
  path: '/orders',
  layout: LayoutMain,
  element: <Orders />,
 },
 {
  path: '/drivers',
  layout: LayoutMain,
  element: <Drivers />,
 },
 {
  path: '/vehicles',
  layout: LayoutMain,
  element: <Vehicles />,
 },
 {
  path: '/products',
  layout: LayoutMain,
  element: <Products />,
 },
 {
  path: '/products/groups',
  layout: LayoutMain,
  element: <ProductsGroups />,
 },
 {
  path: '/spy',
  layout: LayoutMain,
  element: <Spy />,
 },
 {
  path: '/destPoint',
  layout: LayoutMain,
  element: <DestPoint />,
 },
 {
  path: '/destPoint/details',
  layout: LayoutMain,
  element: <DestPointDetails />,
 },
 {
  path: '/cards',
  layout: LayoutMain,
  element: <Cards />,
 },
 {
  path: '/dashboard',
  layout: LayoutMain,
  element: <Dashboard />,
 },
 {
  path: '/admin/dashboard',
  layout: LayoutAdmin,
  element: <AdminDashboard />,
 },
 {
  path: '/admin/licenses',
  layout: LayoutAdmin,
  element: <AdminLicenses />,
 },
 {
  path: '/admin/history',
  layout: LayoutAdmin,
  element: <AdminHistory />,
 },
 {
  path: '/admin/users',
  layout: LayoutAdmin,
  element: <AdminUsers />,
 },
 {
  path: '/admin/accessGroups',
  layout: LayoutAdmin,
  element: <AdminAccessGroups />,
 },
 {
  path: '/admin/audit',
  layout: LayoutAdmin,
  element: <AdminAudit />,
 },
 // 404 page with MinimalLayout
 {
  path: '*',
  element: <ErrorPage />,
  layout: LayoutEmpty,
  requiresAuth: false,
 },
];

// Protected Route component

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
 // const { isAuthenticated } = useAuth();

 const location = useLocation();

 const isAuthenticated = true;

 if (!isAuthenticated) {
  // Redirect to login if not authenticated

  return <Navigate to="/login" state={{ from: location }} replace />;
 }

 return children;
};

// Recursive function to generate routes

export const generateRoutes = (routesList: RouterType[]) =>
 routesList.map((route) => {
  const RouteLayout = route.layout;

  const RouteElement = route.element;

  if (route.children) {
   return (
    <Route
     key={route.path}
     path={route.path}
     errorElement={<ErrorPage />}
     element={
      route.requiresAuth ? (
       <ProtectedRoute>
        <RouteLayout>{RouteElement}</RouteLayout>
       </ProtectedRoute>
      ) : (
       <RouteLayout>{RouteElement}</RouteLayout>
      )
     }
    >
     {generateRoutes(route.children)}
    </Route>
   );
  }

  return (
   <Route
    key={route.path}
    path={route.path}
    element={
     route.requiresAuth ? (
      <ProtectedRoute>
       <RouteLayout>{RouteElement}</RouteLayout>
      </ProtectedRoute>
     ) : (
      <RouteLayout>{RouteElement}</RouteLayout>
     )
    }
   />
  );
 });

